import { AfterContentInit, Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogModule, MatDialogTitle } from '@angular/material/dialog';
import { ChartModule } from 'primeng/chart';
import type { ChartData, ChartOptions } from 'chart.js';
import { GraphItem } from '../../../interfaces/GraphItem';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MetricType } from '../../../interfaces/MetricType';
import { TimeUnit } from '../../../enums/TimeUnit';
import { secondsTo } from '../../../utils/utils';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ERROR } from '../../../shared/strings';
import { NotifyDialogService } from '../../../services/notify-dialog.service';

export interface GraphOveralData {
  graphData: GraphItem[];
  metricsTypes: MetricType[];
  getDataFunction: Function;
}

@Component({
  selector: 'graph-dialog',
  standalone: true,
  imports: [
    ChartModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './graph-dialog.component.html',
  styleUrl: './graph-dialog.component.scss'
})
export class GraphDialogComponent implements AfterContentInit{

  constructor(private notifyDialogService: NotifyDialogService){}

  ERROR = ERROR;
  data = inject<GraphOveralData>(MAT_DIALOG_DATA);
  graphData!: GraphItem[];

  allMetricsTypes!: MetricType[];
  currentMetricType!: MetricType;

  getDataFunction!: Function;

  currentTimeUnit: TimeUnit = TimeUnit.HOUR;
  timeValues: { name: string, enum: TimeUnit}[] = [
    { name: "Weeks", enum: TimeUnit.WEEKS },
    { name: "Days", enum: TimeUnit.DAY },
    { name: "Hours", enum: TimeUnit.HOUR },
    { name: "minutes", enum: TimeUnit.MINUTES }
  ];

  currentUnit!: string;

  isLoading: boolean = false;

  chartData: ChartData<'line'> = {
    labels: [],
    datasets: []
  };

  chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom'
      },
      tooltip: {
        enabled: true
      }
    },
    interaction: {
      mode: 'index',
      intersect: false
    },
    scales: {
      x: {
        title: { display: true, text: 'Day' }
      },
      y: {
        title: { display: true, text: 'Time' }
      }
    }
  };

  ngAfterContentInit(): void {
    this.allMetricsTypes = this.data.metricsTypes;
    this.currentMetricType = this.allMetricsTypes[0];
    this.getDataFunction = this.data.getDataFunction;
    this.currentUnit = this.currentMetricType.measures[0].symbol;
    this.obtainData(this.currentMetricType);
  }

  obtainData(showMetric: MetricType){
    this.isLoading = true;
    this.currentMetricType = showMetric;
    this.currentUnit = this.currentMetricType.measures[0].symbol;
    this.getDataFunction(showMetric).subscribe({
      next: (graphData: GraphItem[])=>{
        this.graphData = graphData;
        this.calculateDataset();
        this.configureGraph();
        this.isLoading = false;
      },
      error: (err: any)=>{
        this.notifyDialogService.openDialog(ERROR, "Error While Getting Alert Documents "+ err);
        this.isLoading = false;
      }
    });
  }

  onChangeTimeUnit(event: MatSelectChange) {
    this.currentTimeUnit = event.value;
    this.calculateDataset();
  }

  calculateDataset(){
    let min = this.graphData.map((item: GraphItem)=>secondsTo(item.minExecutionTime, this.currentTimeUnit));
    let max = this.graphData.map((item: GraphItem)=>secondsTo(item.maxExecutionTime, this.currentTimeUnit));
    let avr = this.graphData.map((item: GraphItem)=>secondsTo(item.avgExecutionTime, this.currentTimeUnit));
    let labels = this.graphData.map((item: GraphItem)=>item.xValue);
    let sets = [min, max, avr];
    let datasetLabel = ["Min", "Max", "Average"];
    let dataset = sets.map((set: number[], i: number)=>{
      return {
        label: datasetLabel[i],
        data: set,
        fill: false,
        tension: 0.4
      }
    });
    this.chartData = {
      labels: labels,
      datasets: dataset
    }
  }

  configureGraph() {
    const rs = getComputedStyle(document.documentElement);
    const chartText = rs.getPropertyValue('--chart-text').trim();
    const chartGrid = rs.getPropertyValue('--chart-grid').trim();
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom'
        },
        tooltip: {
          enabled: true
        }
      },
      interaction: {
        mode: 'index',
        intersect: false
      },
      scales: {
        x: {
          ticks: { color: chartText },
          grid: { color: chartGrid },
          title: { display: true, text: this.currentMetricType.xAxisName, color: chartText }
        },
        y: {
          ticks: { color: chartText },
          grid: { color: chartGrid },
          title: { display: true, text: this.currentMetricType.yAxisName, color: chartText }
        }
      }
    }
  }
}
